import "./OperatorTrendChart.css";

/**
 * Phase 18 WU-18.E3 — multi-operator trend chart.
 *
 * Supports up to 5 operators plotted over the same time axis. Metric picker
 * is a prop — avgKpm / docCount / correctionRate / avgTimePerFieldMs.
 *
 * Uses a custom SVG (not the shared SvgLineChart) because we need multiple
 * lines + a legend with color swatches keyed to each operator.
 */
export interface OperatorTrendPoint {
  day: string;
  docCount: number;
  avgKpm: number;
  correctionRate: number;
  avgTimePerFieldMs: number;
}

export interface OperatorTrendSeries {
  operatorId: string;
  operatorName: string;
  points: OperatorTrendPoint[];
}

export type OperatorTrendMetric =
  | "avgKpm"
  | "docCount"
  | "correctionRate"
  | "avgTimePerFieldMs";

export interface OperatorTrendChartProps {
  series: OperatorTrendSeries[];
  metric: OperatorTrendMetric;
}

const PALETTE = [
  "var(--chart-series-1)",
  "var(--chart-series-3)",
  "var(--chart-series-2)",
  "var(--chart-series-4)",
  "var(--chart-series-5)",
];
const MAX_LINES = 5;

const PAD_LEFT = 48;
const PAD_RIGHT = 16;
const PAD_TOP = 16;
const PAD_BOTTOM = 36;
const WIDTH = 600;
const HEIGHT = 220;

function extract(p: OperatorTrendPoint, m: OperatorTrendMetric): number {
  switch (m) {
    case "avgKpm":
      return p.avgKpm;
    case "docCount":
      return p.docCount;
    case "correctionRate":
      return Math.round(p.correctionRate * 1000) / 10; // percent
    case "avgTimePerFieldMs":
      return p.avgTimePerFieldMs;
  }
}

export function OperatorTrendChart({ series, metric }: OperatorTrendChartProps) {
  const truncated = series.slice(0, MAX_LINES);
  const allDays = Array.from(
    new Set(truncated.flatMap((s) => s.points.map((p) => p.day))),
  ).sort();

  if (truncated.length === 0 || allDays.length === 0) {
    return (
      <div className="operator-trend__empty" data-testid="operator-trend-empty">
        No trend data for this period.
      </div>
    );
  }

  const chartW = WIDTH - PAD_LEFT - PAD_RIGHT;
  const chartH = HEIGHT - PAD_TOP - PAD_BOTTOM;

  const maxVal = Math.max(
    1,
    ...truncated.flatMap((s) => s.points.map((p) => extract(p, metric))),
  );

  const toX = (i: number) =>
    PAD_LEFT + (allDays.length === 1 ? chartW / 2 : (i / (allDays.length - 1)) * chartW);
  const toY = (v: number) => PAD_TOP + chartH - (v / maxVal) * chartH;

  return (
    <div className="operator-trend">
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        style={{ width: "100%", height: "auto", display: "block" }}
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Axis */}
        <line
          x1={PAD_LEFT}
          y1={PAD_TOP}
          x2={PAD_LEFT}
          y2={PAD_TOP + chartH}
          style={{ stroke: "var(--color-border)" }}
        />
        <line
          x1={PAD_LEFT}
          y1={PAD_TOP + chartH}
          x2={PAD_LEFT + chartW}
          y2={PAD_TOP + chartH}
          style={{ stroke: "var(--color-border)" }}
        />
        {truncated.map((s, idx) => {
          const color = PALETTE[idx] ?? "var(--chart-series-1)";
          // Build path across all days; skip days with no point.
          const dayToValue = new Map(s.points.map((p) => [p.day, extract(p, metric)]));
          const pathSegments: string[] = [];
          let started = false;
          allDays.forEach((day, i) => {
            if (!dayToValue.has(day)) {
              started = false;
              return;
            }
            const v = dayToValue.get(day) ?? 0;
            const cmd = started ? "L" : "M";
            pathSegments.push(`${cmd} ${toX(i).toFixed(1)} ${toY(v).toFixed(1)}`);
            started = true;
          });
          return (
            <path
              key={s.operatorId}
              d={pathSegments.join(" ")}
              fill="none"
              style={{ stroke: color }}
              strokeWidth="2"
              strokeLinejoin="round"
              strokeLinecap="round"
              data-testid={`operator-trend-line-${s.operatorId}`}
            />
          );
        })}
      </svg>
      <div className="operator-trend__legend">
        {truncated.map((s, idx) => {
          const color = PALETTE[idx] ?? "var(--chart-series-1)";
          return (
            <div key={s.operatorId} className="operator-trend__legend-item">
              <span className="operator-trend__swatch" style={{ background: color }} />
              {s.operatorName}
            </div>
          );
        })}
      </div>
    </div>
  );
}
